(ns common.opensearch.read-gates
  "Every dedicated-index entity type's read gate, declared once as data.

  Two consumers derive from this, and the whole point of the namespace is that
  they cannot disagree:

  - `backend.search.entity-indices/get-permissions-fn-maker` builds the
    per-document `permissions.accounts` ACL from it. A type whose gate is
    account-level declares no `:permissions-fn-maker` anywhere; the maker is
    derived. Declaring both is refused at boot by
    `backend.schema.definition-validation`.
  - the field-path gate in `common.opensearch.access`, which decides whether a
    root document may expose a more strictly gated TARGET type's flattened
    fields. That consumer arrives with the reconciliation work; see
    ai/project/release-audit-remediation/handoff.md.

  Facets come in two classes, and the split is what makes the second consumer
  possible at all:

  - ACCOUNT-LEVEL - `:gate/all-users?`, `:gate/modules`, `:gate/features`.
    Decidable from tenant, role and the account's own permissions, so the pure
    cljc field-path layer can evaluate them with the arguments it already
    receives. Every listed module and feature is required (conjunction).
  - DOCUMENT-LEVEL - `:gate/document`, whose value names WHICH per-document
    dimension decides access (pipeline team membership, fund teams, the
    document's own team assignments, ...). Structurally undecidable in the
    field-path layer, which has no document in hand, so that layer must treat
    any `:gate/document` as \"cannot decide\" and fail closed. The marker is
    deliberately descriptive rather than executable: these types keep their
    hand-written `:permissions-fn-maker`, because gates like
    project-counterparty-diligence's admin-union-light-access-intersect-pipeline
    or task's one-tenant assignee carve-out are not expressible as data worth
    inventing a language for.

  A gate is the type's own read gate, i.e. what its OWN listing requires. It is
  not a statement about what a document that merely REFERENCES this type may
  expose - that is a property of the relationship, declared per relationship.

  Admins are outside all of this: `backend.domain.search.permissions/permissions-queries`
  only adds the `permissions.accounts` clause for non-admins, so an admin reads
  every document regardless of what is declared here."
  (:require
   [clojure.set :as set]))

(def document-gate-markers
  "Every legal `:gate/document` value. Each names the per-document dimension a
  hand-written `:permissions-fn-maker` implements, so a typo in a gate
  declaration fails at boot instead of silently reading as \"no marker\"."
  #{:document.gate/pipeline-team
    :document.gate/pipeline-own-teams
    :document.gate/fund-teams
    :document.gate/fund-commitment-fund-teams
    :document.gate/insider-group-membership
    :document.gate/source-entity-pipeline
    :document.gate/team-assignments
    :document.gate/vault-membership
    :document.gate/task-assignee})

(def entity-type->read-gate
  "Gate per entity type, alphabetical. Every type that
  `backend.search.entity-indices/get-permissions-fn-maker` can answer for must
  appear here; `backend.schema.definition-validation` enforces that at boot."
  {:entity.type/account                        {:gate/all-users? true}
   :entity.type/calculated-indirect-holding    {:gate/modules [:module/portfolio]}
   :entity.type/check-list                     {:gate/all-users? true}
   :entity.type/company                        {:gate/all-users? true}
   :entity.type/company-counterparty-diligence {:gate/features [:feature/counterparty-diligence]
                                                :gate/modules  [:module/counterparty-diligence-admin]}
   :entity.type/contact-list                   {:gate/modules [:module/contact-lists]}
   :entity.type/contract-kyc-profile           {:gate/features [:feature/contract-kyc]
                                                :gate/modules  [:module/contract-kyc]}
   :entity.type/email-template                 {:gate/all-users? true}
   :entity.type/exit-opportunity               {:gate/document :document.gate/pipeline-team}
   :entity.type/exit-opportunity-stage         {:gate/all-users? true}
   :entity.type/fee                            {:gate/modules [:module/fee-tracking]}
   :entity.type/fof-asset                      {:gate/all-users? true}
   :entity.type/fund                           {:gate/document :document.gate/fund-teams}
   :entity.type/fund-commitment                {:gate/document :document.gate/fund-commitment-fund-teams}
   :entity.type/fundraising-opportunity        {:gate/document :document.gate/pipeline-team}
   :entity.type/graph-company                  {:gate/modules [:module/entity-graph]}
   :entity.type/graph-config                   {:gate/modules [:module/entity-graph]}
   :entity.type/graph-fof-asset                {:gate/modules [:module/entity-graph]}
   :entity.type/graph-fund                     {:gate/modules [:module/entity-graph]}
   :entity.type/graph-person                   {:gate/modules [:module/entity-graph]}
   :entity.type/holding                        {:gate/modules [:module/portfolio]}
   :entity.type/insider-group                  {:gate/document :document.gate/insider-group-membership}
   :entity.type/investment-reservation-type    {:gate/all-users? true}
   :entity.type/investor                       {:gate/modules [:module/fundraising]}
   :entity.type/kpi-category                   {:gate/modules [:module/kpi-reporting]}
   ;; Deliberately the INTENDED gate, which is not the one running today.
   ;; backend.domain.entities.kyc-profile.core declares
   ;; feature-and-module-access-permissions-fn for :feature/kyc + :module/kyc,
   ;; but entity_type_registry.clj's require for kyc-profile aliases the CLJC
   ;; namespace where every sibling aliases its backend one, so that whole
   ;; :search map is dropped and the type falls back to the legacy
   ;; module-only partial. Declaring module-only here to match would ratify a
   ;; fail-open: for a tenant without :feature/kyc, every :module/kyc holder
   ;; currently reads kyc-profile documents the intended gate denies. This is
   ;; the one deliberate behaviour change in an otherwise behaviour-neutral
   ;; derivation - see ai/mistakes.md, "kyc-profile's backend entity
   ;; definition was never registered".
   :entity.type/kyc-profile                    {:gate/features [:feature/kyc]
                                                :gate/modules  [:module/kyc]}
   :entity.type/kyc-risk-category              {:gate/all-users? true}
   :entity.type/liability                      {:gate/modules [:module/liabilities]}
   :entity.type/lp-news-item                   {:gate/modules [:module/lp-reporting]}
   :entity.type/lp-promotion                   {:gate/modules [:module/lp-reporting]}
   ;; :module/metabase-reporting-db is absent from common.modules/modules, so
   ;; no account can hold it and this gate admits no non-admin at all. Kept
   ;; as-is rather than "corrected" to :module/dashboards: it fails closed, and
   ;; whether this listing is meant to be admin-only is a product question, not
   ;; something to change while collapsing declarations into data.
   :entity.type/metabase-dashboard             {:gate/modules [:module/metabase-reporting-db]}
   :entity.type/named-value                    {:gate/all-users? true}
   :entity.type/named-view                     {:gate/all-users? true}
   :entity.type/oppo                           {:gate/document :document.gate/pipeline-team}
   :entity.type/opportunity-stage              {:gate/all-users? true}
   :entity.type/other-asset                    {:gate/all-users? true}
   :entity.type/person                         {:gate/all-users? true}
   :entity.type/person-counterparty-diligence  {:gate/features [:feature/counterparty-diligence]
                                                :gate/modules  [:module/counterparty-diligence-admin]}
   :entity.type/pipeline                       {:gate/document :document.gate/pipeline-own-teams}
   :entity.type/project-counterparty-diligence {:gate/document :document.gate/source-entity-pipeline}
   :entity.type/real-estate                    {:gate/all-users? true}
   :entity.type/risk                           {:gate/modules [:module/risk-module]}
   :entity.type/risk-holding                   {:gate/modules [:module/risk-module]}
   :entity.type/role                           {:gate/all-users? true}
   :entity.type/sale-agreement                 {:gate/modules [:module/sales-pipelines]}
   :entity.type/sales-opportunity              {:gate/document :document.gate/pipeline-team}
   ;; Byte-identical to its three sibling stage types below. It was the one
   ;; member of that family declaring nothing, which left its documents with
   ;; `permissions []` and invisible to every non-admin - see ai/mistakes.md,
   ;; "Ten dedicated-index entity types had no read ACL at all".
   :entity.type/sales-opportunity-stage        {:gate/all-users? true}
   :entity.type/sample-entity                  {:gate/all-users? true}
   :entity.type/screening-profile              {:gate/features [:feature/screening]
                                                :gate/modules  [:module/screening]}
   :entity.type/stage                          {:gate/all-users? true}
   :entity.type/survey-block                   {:gate/all-users? true}
   :entity.type/survey-question-ext-id         {:gate/all-users? true}
   :entity.type/survey-question-option         {:gate/all-users? true}
   :entity.type/survey-template                {:gate/all-users? true}
   :entity.type/survey-type                    {:gate/all-users? true}
   ;; All-users for every tenant except the one literally named "CapMan",
   ;; where only a task's assignee may see it - a per-document read, hence a
   ;; document gate rather than :gate/all-users?.
   :entity.type/task                           {:gate/document :document.gate/task-assignee}
   :entity.type/tenant-kyc-profile             {:gate/features [:feature/tenant-kyc]
                                                :gate/modules  [:module/tenant-kyc]}
   :entity.type/transaction-process            {:gate/document :document.gate/team-assignments}
   :entity.type/value-creation                 {:gate/modules [:module/value-creation-activity]}
   :entity.type/view                           {:gate/all-users? true}
   :entity.type/webentry                       {:gate/all-users? true}
   :entity.type/zap-vault                      {:gate/document :document.gate/vault-membership}})

(def gateless-dedicated-index-entity-types
  "Types with a dedicated `entity_*` index that declare NO read gate, and did
  not declare a `:permissions-fn-maker` before gates existed either.

  Every one of them therefore resolves to
  `backend.domain.entities.permissions/make-account-permissions-lookup-fn`'s
  `(fn [_] [])` fallback, so their documents carry `:permissions []` - no
  `permissions.accounts` at all, which
  `backend.domain.search.permissions/permissions-queries` turns into a term
  clause matching nothing for a non-admin. They are invisible to every
  non-admin today. Fail-closed, so not a leak, but nothing was checking it.

  Listed explicitly rather than tolerated by a laxer rule: a type added to a
  dedicated index from now on must declare a gate or `validate-read-gates!`
  refuses to boot. Shrinking this list is a behaviour change per entry -
  granting access where none is granted now - so each needs its own decision,
  not a blanket sweep.

  `:entity.type/sales-opportunity-stage` used to head this list and has since
  been gated `{:gate/all-users? true}` like its three sibling stage types. Note
  what the investigation actually found, because the reasoning generalises to
  the entries still here: the missing ACL was real - its documents did ship
  `permissions []` where the siblings ship `{:accounts [...]}` - but the symptom
  predicted for it, an empty sales kanban for non-admins, was wrong. Nothing
  reads stage data from OpenSearch at all; kanban columns come from the Datomic
  sync cache. So an entry on this list is not evidence that anything is broken,
  only that nothing is granted. Establish the reader before predicting a
  symptom.

  A type must not appear both here and in `entity-type->read-gate`;
  `backend.schema.definition-validation` refuses to boot if it does, so this
  list cannot silently keep an entry that has since been gated."
  #{:entity.type/advisory-board-decision
    :entity.type/aifm-decision
    :entity.type/aifm-exit-decision
    :entity.type/esign-process
    :entity.type/ic-decision
    :entity.type/ic-exit-decision
    :entity.type/job
    :entity.type/pm-approval-decision
    :entity.type/survey-question})

(def ^:private account-level-facets
  #{:gate/all-users? :gate/modules :gate/features})

(defn read-gate
  [entity-type]
  (get entity-type->read-gate entity-type))

(defn document-gate
  "The `:gate/document` marker, or nil for an account-level gate."
  [gate]
  (:gate/document gate))

(defn account-level-gate?
  "True when every facet of `gate` is decidable without a document in hand.
  A gate with no recognized facet at all is NOT account-level - an empty or
  unknown declaration must never read as \"nothing required\"."
  [gate]
  (boolean (and (map? gate)
                (nil? (document-gate gate))
                (seq (set/intersection account-level-facets (set (keys gate)))))))
